// Pure helpers for split shipping (Stephanie 2026-10-16) — no React, no app
// imports, so the offline gate can run them.

export interface SplitVariant {
  id: string;
  color: string | null;
  size: string | null;
  quantity: number;
}

export interface SplitDraftRow {
  addressId: string;
  /** variantId → qty (items with sizes). */
  qty: Record<string, number>;
  /** Items without sizes. */
  total: number;
}

export interface SavedSplit {
  addressId: string;
  addressSnapshot: Record<string, unknown>;
  quantities: Array<{ variantId: string; color: string | null; size: string | null; qty: number }>;
  quantity: number;
}

/** How many of each size are still to place (negative = too many). */
export function remainingBySize(variants: readonly SplitVariant[], rows: readonly SplitDraftRow[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const v of variants) out[v.id] = v.quantity - rows.reduce((n, r) => n + (r.qty[v.id] ?? 0), 0);
  return out;
}

/** Pieces still to place in total (items without sizes use `total`). */
export function remainingTotal(quantityTotal: number, variants: readonly SplitVariant[], rows: readonly SplitDraftRow[]): number {
  if (variants.length > 0) return Object.values(remainingBySize(variants, rows)).reduce((a, b) => a + b, 0);
  return quantityTotal - rows.reduce((n, r) => n + (r.total || 0), 0);
}

/** Pure: is the draft ready to save? (Mirrors the server's checkSplit.) */
export function draftProblems(quantityTotal: number, variants: readonly SplitVariant[], rows: readonly SplitDraftRow[]): string[] {
  const p: string[] = [];
  if (rows.length < 2) p.push("Add at least two addresses.");
  if (rows.some((r) => !r.addressId)) p.push("Pick an address on every row.");
  if (new Set(rows.map((r) => r.addressId)).size !== rows.length) p.push("Each address can only be listed once.");
  const pieces = (r: SplitDraftRow) => (variants.length ? Object.values(r.qty).reduce((a, b) => a + (b || 0), 0) : r.total || 0);
  if (rows.some((r) => pieces(r) === 0)) p.push("Every address needs at least one piece.");
  const left = remainingTotal(quantityTotal, variants, rows);
  const over = variants.length ? Object.values(remainingBySize(variants, rows)).some((n) => n < 0) : left < 0;
  if (over) p.push("More pieces placed than the item has.");
  else if (left > 0) p.push(`${left} still to place.`);
  return p;
}

/** One line for the Shipping tab: "Baltimore 24 · Washington 24". */
export function splitSummary(splits: readonly SavedSplit[]): string {
  return splits
    .map((s) => {
      const a = s.addressSnapshot as { label?: string; city?: string };
      return `${a.label || a.city || "Address"} ${s.quantity}`;
    })
    .join(" · ");
}

/** A saved split back into editable rows. */
export function toDraft(splits: readonly SavedSplit[]): SplitDraftRow[] {
  return splits.map((s) => ({
    addressId: s.addressId,
    qty: Object.fromEntries(s.quantities.map((q) => [q.variantId, q.qty])),
    total: s.quantity,
  }));
}

/** "Baltimore office — Baltimore, MD" for a ship-to snapshot. */
export function placeLabel(snapshot: unknown): string {
  const a = (snapshot ?? {}) as Record<string, unknown>;
  const s = (k: string) => (typeof a[k] === "string" && (a[k] as string).trim() ? (a[k] as string).trim() : null);
  const name = s("companyName") ?? s("label") ?? s("name");
  const place = [s("city"), s("stateCode") ?? s("state")].filter(Boolean).join(", ");
  return [name, place].filter(Boolean).join(" — ") || "Ship-to address";
}

/** Where a PO's boxes can go: its own ship-to, plus every split address on
 *  its items (each once). */
export function shipDestinations(
  po: { addressId: string | null; snapshot: unknown },
  splits: Record<string, Array<{ addressId: string; addressSnapshot: unknown }>> | undefined,
): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = [];
  const seen = new Set<string>();
  const add = (id: string | null, snap: unknown) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push({ id, label: placeLabel(snap) });
  };
  for (const rows of Object.values(splits ?? {})) for (const r of rows) add(r.addressId, r.addressSnapshot);
  if (out.length === 0) add(po.addressId, po.snapshot);
  return out;
}

/** "3 boxes · 2 delivered" for the tracking table (Stephanie 2026-10-19);
 *  "1" for a single box. */
export function boxesText(count: number | null | undefined, delivered: number | null | undefined, status: string): string {
  const n = count && count > 1 ? count : 1;
  if (n === 1) return "1";
  if (status === "delivered") return `${n} boxes · all delivered`;
  if (delivered != null && delivered > 0) return `${n} boxes · ${delivered} delivered`;
  return `${n} boxes`;
}
